import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  AlertTitle,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Close as CloseIcon,
  TableChart as TableChartIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";

/**
 * Represents the value of a single cell in the spreadsheet
 * @typedef {string | number | boolean | null | undefined} CellValue
 */
type CellValue = string | number | boolean | null | undefined;

/**
 * Represents a row of data in the spreadsheet
 * @typedef {object} RowData
 * @property {CellValue} [key] - The cell values indexed by column key
 * @property {string | number} [id] - Optional unique identifier for the row
 * @property {string | number} [_id] - Alternative unique identifier for the row
 */
interface RowData {
  [key: string]: CellValue;
  id?: string | number;
  _id?: string | number;
}

/**
 * Defines the structure of a column in the spreadsheet
 * @interface ColumnDefinition
 * @property {string} label - The display name of the column
 * @property {string} key - A unique identifier for the column
 * @property {number} [width] - Optional width of the column in pixels
 * @property {boolean} [numeric] - Whether the column contains numeric data
 */
interface ColumnDefinition {
  label: string;
  key: string;
  width?: number;
  numeric?: boolean;
}

/**
 * Props for the SpreadsheetViewer component
 * @interface SpreadsheetViewerProps
 * @property {string} fileUrl - The URL of the spreadsheet file to display
 * @property {string} fileName - The name of the file to display in the header
 * @property {() => void} onClose - Callback function to handle closing the viewer
 */
interface SpreadsheetViewerProps {
  fileUrl: string;
  fileName: string;
  onClose: () => void;
}

/**
 * Represents the data and metadata for a single sheet in the spreadsheet
 * @interface SheetData
 * @property {string} name - The name of the sheet
 * @property {RowData[]} data - The data rows in the sheet
 * @property {ColumnDefinition[]} columns - The column definitions for the sheet
 * @property {number} rowCount - The number of rows in the sheet
 */
interface SheetData {
  name: string;
  data: RowData[];
  columns: ColumnDefinition[];
  rowCount: number;
}

/**
 * Represents an error that occurred while loading or processing the spreadsheet
 * @interface SpreadsheetError
 * @property {string} title - A brief title for the error
 * @property {string} message - A user-friendly error message
 * @property {string} [details] - Additional details about the error (optional)
 */
interface SpreadsheetError {
  title: string;
  message: string;
  details?: string;
}

/**
 * A React component for displaying spreadsheet files (XLSX, XLS, CSV) with support for multiple sheets.
 * Provides a tabbed interface to navigate between sheets and a responsive table view for data display.
 * 
 * @component
 * @param {SpreadsheetViewerProps} props - The component props
 * @returns {JSX.Element} A dialog containing the spreadsheet viewer
 * 
 * @example
 * <SpreadsheetViewer
 *   fileUrl="https://example.com/spreadsheet.xlsx"
 *   fileName="Financial Report.xlsx"
 *   onClose={() => console.log('Viewer closed')}
 * />
 */
const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  fileUrl,
  fileName,
  onClose,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<SpreadsheetError | null>(null);
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);

  /**
   * Converts a cell value to a standardized format for display
   * @function toRowData
   * @param {unknown} cell - The cell value to convert
   * @returns {CellValue} The converted cell value
   * @private
   */

  const handlePrimitiveValue = (value: unknown): CellValue => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
    return null;
  };

  /**
   * Handles complex object values by converting them to a string representation
   * @param obj - The object to convert
   * @returns A string representation of the object
   */
  const handleObjectValue = (obj: object): string => {
    try {
      // Handle arrays
      if (Array.isArray(obj)) {
        return `[${obj.map(item => 
          typeof item === 'object' 
            ? handleObjectValue(item) 
            : JSON.stringify(item)
        ).join(', ')}]`;
      }
      
      // Handle plain objects
      if (obj !== null && typeof obj === 'object') {
        const entries = Object.entries(obj).map(([key, value]) => {
          const valStr = typeof value === 'object' 
            ? handleObjectValue(value) 
            : JSON.stringify(value);
          return `"${key}": ${valStr}`;
        });
        return `{${entries.join(', ')}}`;
      }
      
      // Fallback for other object types
      return String(obj);
    } catch (error) {
      console.error('Error handling object value:', error);
      return '[Object]';
    }
  };

  /**
   * Handles complex values by converting them to a string representation
   * @param value - The value to convert
   * @returns A string representation of the value
   */
  const handleComplexValue = (value: unknown): string => {
    try {
      if (value === null || value === undefined) return 'null';
      
      // Handle Date objects
      if (value instanceof Date) {
        return isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
      }
      
      // Handle arrays and plain objects
      if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
        return handleObjectValue(value);
      }
      
      // Try to use the object's toString() method if it's not the default
      if (typeof value === 'object' && value !== null) {
        const str = value.toString();
        if (str !== '[object Object]' && str !== '[object Array]') {
          return str;
        }
      }
      
      // Fallback for other types
      return String(value);
    } catch (error) {
      console.error('Unexpected error in handleComplexValue:', error);
      return '[Error]';
    }
  };

  const toRowData = useCallback((cell: unknown): CellValue => {
    try {
      // Handle primitive values first (fast path)
      const primitive = handlePrimitiveValue(cell);
      if (primitive !== null) {
        return primitive;
      }
      
      // Handle complex values
      return handleComplexValue(cell);
    } catch (e) {
      console.error('Error processing cell value:', e);
      return 'Unable to display content';
    }
  }, []);

  /**
   * Processes the XLSX workbook data into a structured format for display
   * @function processWorkbook
   * @param {XLSX.WorkBook} workbook - The parsed XLSX workbook
   * @returns {SheetData[]} An array of processed sheet data
   * @private
   */
  const processWorkbook = useCallback(
    (workbook: XLSX.WorkBook): SheetData[] => {
      const parsedSheets: SheetData[] = [];

      for (const sheetName of workbook.SheetNames) {
        const worksheet = workbook.Sheets[sheetName];
        if (!worksheet) continue;

        // Convert sheet to JSON with headers
        const jsonData =
          XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
            header: 1,
            defval: null,
            blankrows: false,
          }) || [];

        // Filter out empty rows and ensure proper typing
        const rows = jsonData.filter(
          (row): row is unknown[] =>
            Array.isArray(row) &&
            row.some(
              (cell) => cell !== null && cell !== undefined && cell !== ""
            )
        );

        if (rows.length === 0) {
          parsedSheets.push({
            name: sheetName,
            data: [],
            columns: [],
            rowCount: 0,
          });
          continue;
        }

        // Use first row as headers if it contains strings, otherwise generate headers
        const firstRow = rows[0] || [];
        const hasStringHeaders = firstRow.some(
          (cell) => typeof cell === "string" && String(cell).trim() !== ""
        );

        let headers: ColumnDefinition[] = [];
        let dataRows: RowData[] = [];

        if (hasStringHeaders) {
          // Use first row as headers
          headers = firstRow.map((header, index) => ({
            label:
              header !== null && header !== undefined
                ? String(header)
                : `Column ${index + 1}`,
            key: `col${index}`,
            width: 150, // Default width
          }));

          // Convert remaining rows to RowData
          dataRows = rows.slice(1).map((row) => {
            const rowData: RowData = {};
            row.forEach((cell, index) => {
              rowData[`col${index}`] = toRowData(cell);
            });
            return rowData;
          });
        } else {
          // Generate default headers
          const maxCols = rows.reduce(
            (max, row) => Math.max(max, row.length),
            0
          );
          headers = Array.from({ length: maxCols }, (_, i) => ({
            label: `Column ${i + 1}`,
            key: `col${i}`,
            width: 150, // Default width
          }));

          // Convert all rows to RowData
          dataRows = rows.map((row) => {
            const rowData: RowData = {};
            row.forEach((cell, index) => {
              rowData[`col${index}`] = toRowData(cell);
            });
            return rowData;
          });
        }

        parsedSheets.push({
          name: sheetName,
          data: dataRows,
          columns: headers,
          rowCount: dataRows.length,
        });
      }

      return parsedSheets;
    },
    [toRowData]
  );

  // Load and process the spreadsheet file when the component mounts or fileUrl changes
  useEffect(() => {
    /**
     * Loads the spreadsheet file from the provided URL and processes it
     * @async
     * @function loadFile
     * @returns {Promise<void>}
     * @private
     */
    const loadFile = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const data = new Uint8Array(arrayBuffer);

        // Parse the file
        const workbook = XLSX.read(data, { type: "array" });

        // Process the workbook into our SheetData format
        const parsedSheets = processWorkbook(workbook);

        setSheets(parsedSheets);
      } catch (err) {
        console.error("Error loading spreadsheet:", err);
        setError({
          title: "Error Loading Spreadsheet",
          message:
            "Failed to load spreadsheet. Please check the file URL and try again.",
          details: err instanceof Error ? err.message : String(err),
        });
      } finally {
        setLoading(false);
      }
    };

    loadFile();
  }, [fileUrl, processWorkbook]);

  // Handle tab change
  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    []
  );

  // Render the table for the current sheet
  const renderTable = useCallback((sheet: SheetData) => {
    if (!sheet.data || sheet.data.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" color="textSecondary">
            No data available in this sheet.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: "#fff",
          "& .MuiTableContainer-root": {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
            overflow: "auto",
            border: "1px solid #e0e0e0",
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "0",
              "&:hover": {
                background: "#a8a8a8",
              },
            },
          },
          "& .MuiTable-root": {
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
          },
          "& .MuiTableCell-root": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "300px",
            padding: "4px 8px",
            border: "1px solid #e0e0e0",
            lineHeight: "1.5",
            verticalAlign: "middle",
            "&:first-of-type": {
              borderLeft: "none",
            },
            "&:last-child": {
              borderRight: "none",
            },
          },
          "& .MuiTableHead .MuiTableCell-root": {
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#f8f9fa",
            borderBottom: "2px solid #e0e0e0",
            fontWeight: 600,
            color: "#202124",
            padding: "6px 8px",
          },
          "& .MuiTableBody .MuiTableRow:nth-of-type(odd)": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
          },
          "& .MuiTableBody .MuiTableRow:hover": {
            backgroundColor: "rgba(66, 133, 244, 0.08)",
          },
          "& .MuiTableBody .MuiTableCell": {
            backgroundColor: "inherit",
          },
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            boxShadow: "none",
            borderRadius: 0,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                fontFamily: 'Lato',
                fontSize: "13px",
              },
            }}
          >
            <TableHead>
              <TableRow>
                {sheet.columns.map((column) => (
                  <TableCell
                    key={`header-${column.key}`}
                    sx={{
                      minWidth: column.width || 120,
                      maxWidth: column.width || 250,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sheet.data.map((row: RowData, rowIndex: number) => {
                // Create a unique key for each row using id, _id, or fallback to index
                const rowKey = row.id ?? row._id ?? `row-${rowIndex}`;
                return (
                  <TableRow
                    key={rowKey}
                    hover
                    sx={{
                      "&:last-child td": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    {sheet.columns.map((column, colIndex) => {
                      const cellValue = Array.isArray(row) ? row[colIndex] : row[column.key];
                      return (
                        <TableCell
                          key={`cell-${rowKey}-${column.key}`}
                          sx={{
                            minWidth: column.width || 120,
                            maxWidth: column.width || 250,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            backgroundColor: "inherit",
                          }}
                        >
                          {cellValue !== null && cellValue !== undefined
                            ? String(cellValue)
                            : ""}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }, []);

  // Render error state
  if (error) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Error Loading Spreadsheet</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{error.title}</AlertTitle>
            {error.message}
            {error.details && (
              <Box
                component="pre"
                sx={{ mt: 2, fontSize: "0.75rem", overflowX: "auto" }}
              >
                {error.details}
              </Box>
            )}
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Render the appropriate content based on loading and data states
  const renderSpreadsheetContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (sheets.length === 0) {
      return (
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No data available in this spreadsheet.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        {sheets.length > 1 && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {sheets.map((sheet, index) => (
              <Tab
                key={sheet.name}
                label={sheet.name}
                id={`sheet-tab-${index}`}
                aria-controls={`sheet-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        )}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          {sheets.map((sheet, index) => (
            <div
              key={sheet.name}
              role="tabpanel"
              hidden={activeTab !== index}
              id={`sheet-tabpanel-${index}`}
              aria-labelledby={`sheet-tab-${index}`}
              style={{ height: "100%" }}
            >
              {activeTab === index && renderTable(sheet)}
            </div>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          style: {
            height: "90vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            aria-label="back"
            onClick={onClose}
            sx={{
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <TableChartIcon sx={{ color: "white" }} />
          <Typography
            variant="h6"
            noWrap
            sx={{ maxWidth: "600px", color: "white" }}
          >
            {fileName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          p: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0, // Important for proper scrolling
          "&.MuiDialogContent-root": {
            padding: 0,
          },
        }}
      >
        {renderSpreadsheetContent()}
      </DialogContent>
    </Dialog>
  );
};

export default SpreadsheetViewer;
